import {
  DEFAULT_STORAGE_LIMITS,
  StorageValidationError,
  type JsonObject,
  type JsonValue,
  type ProjectMetadataInput,
  type StorageLimits,
} from "./types.js";

const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

/** `instanceof` is insufficient for byte views returned by another browser realm. */
export function isUint8Array(value: unknown): value is Uint8Array {
  return ArrayBuffer.isView(value) && Object.prototype.toString.call(value) === "[object Uint8Array]";
}

export function assertId(value: unknown, field: string): asserts value is string {
  if (typeof value !== "string" || !ID_PATTERN.test(value)) {
    throw new StorageValidationError(`${field} is invalid`);
  }
}

export function validateMetadata(
  input: ProjectMetadataInput,
  limits: StorageLimits = DEFAULT_STORAGE_LIMITS,
): Required<Pick<ProjectMetadataInput, "title" | "tags" | "notes">> & Pick<ProjectMetadataInput, "thumbnailAssetId"> {
  if (typeof input.title !== "string" || input.title.trim().length === 0 || input.title.length > limits.maxTitleLength) {
    throw new StorageValidationError(`title must contain 1-${limits.maxTitleLength} characters`);
  }
  const tags = input.tags ? [...input.tags] : [];
  if (tags.length > limits.maxTags) throw new StorageValidationError(`A project may have at most ${limits.maxTags} tags`);
  for (const tag of tags) {
    if (typeof tag !== "string" || tag.trim().length === 0 || tag.length > limits.maxTagLength) {
      throw new StorageValidationError(`Each tag must contain 1-${limits.maxTagLength} characters`);
    }
  }
  const notes = input.notes ?? "";
  if (typeof notes !== "string" || notes.length > limits.maxNotesLength) {
    throw new StorageValidationError(`notes may contain at most ${limits.maxNotesLength} characters`);
  }
  if (input.thumbnailAssetId !== undefined) assertId(input.thumbnailAssetId, "thumbnailAssetId");
  const result = { title: input.title.trim(), tags: unique(tags.map((tag) => tag.trim())), notes };
  return input.thumbnailAssetId === undefined ? result : { ...result, thumbnailAssetId: input.thumbnailAssetId };
}

export function normalizeSearch(value: string): string {
  return value.normalize("NFKC").toLocaleLowerCase();
}

export function validateJsonDocument(
  document: JsonObject,
  limits: StorageLimits = DEFAULT_STORAGE_LIMITS,
): void {
  validateJsonTree(document, { maxDepth: 64, maxNodes: 100_000, maxStringLength: 1_000_000 });
  const bytes = new TextEncoder().encode(JSON.stringify(document)).byteLength;
  if (bytes > limits.maxDocumentJsonBytes) {
    throw new StorageValidationError(`Document exceeds ${limits.maxDocumentJsonBytes} bytes`);
  }
}

export function validateJsonTree(
  value: unknown,
  options: { maxDepth: number; maxNodes: number; maxStringLength: number },
): asserts value is JsonValue {
  let nodes = 0;
  const visit = (current: unknown, depth: number): void => {
    nodes += 1;
    if (nodes > options.maxNodes) throw new StorageValidationError("JSON contains too many values");
    if (depth > options.maxDepth) throw new StorageValidationError("JSON nesting is too deep");
    if (current === null || typeof current === "boolean") return;
    if (typeof current === "string") {
      if (current.length > options.maxStringLength) throw new StorageValidationError("JSON string exceeds its limit");
      return;
    }
    if (typeof current === "number") {
      if (!Number.isFinite(current)) throw new StorageValidationError("JSON numbers must be finite");
      return;
    }
    if (Array.isArray(current)) {
      for (const item of current) visit(item, depth + 1);
      return;
    }
    if (typeof current === "object") {
      const prototype: unknown = Object.getPrototypeOf(current);
      if (prototype !== Object.prototype && prototype !== null) {
        throw new StorageValidationError("JSON must contain only plain objects");
      }
      for (const [key, item] of Object.entries(current as Record<string, unknown>)) {
        if (key.length > 256) throw new StorageValidationError("JSON key exceeds its limit");
        visit(item, depth + 1);
      }
      return;
    }
    throw new StorageValidationError("Value is not valid JSON");
  };
  visit(value, 0);
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}
